import { ExternalLink } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { bridge } from '~/services/bridge'
import { useCreditPacks } from '~/billing/hooks/useCreditPacks'
import { useStripeCheckout } from '~/billing/hooks/useStripeCheckout'
import { CreditPackInfo, LicenseTypeResponse } from '~/billing/services/billingService'
import { Button } from '~/components/common/Button'
import { Modal, ModalFooter } from '~/components/modals/CurieModal'
import {
  SegmentControls,
  SegmentControlsList,
  SegmentControlsTrigger
} from '~/components/common/SegmentControls'
import { Typography } from '~/components/common/Typography'
import { useBillingContext } from '~/contexts/BillingContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { LoadingIcon } from '../icons'

type UpgradeTab = 'plan' | 'credits'
type BillingPackage = 'monthly' | 'six_month'
type PackagePricing = NonNullable<
  NonNullable<LicenseTypeResponse['metadata']['pricing']>['packages']
>[string]

const PLAN_DISPLAY_ORDER: Record<string, number> = {
  pro: 1,
  pro_plus: 2,
  team: 3,
  enterprise: 4
}

const PLAN_DESCRIPTIONS: Record<string, string> = {
  pro: 'Essential tools for individuals.',
  pro_plus: 'Faster, smarter building.',
  team: 'Collaborate for max of 5 users.',
  enterprise: 'Scalable for your organization.'
}

const SIX_MONTH_PACKAGE_MONTHS = 6
const DEFAULT_SIX_MONTH_DISCOUNT_PERCENT = 15

const getPackagePricing = (
  pricing: LicenseTypeResponse['metadata']['pricing'],
  billingPackage: BillingPackage
): PackagePricing | null => {
  if (billingPackage === 'monthly') return null
  return pricing?.packages?.[billingPackage] ?? null
}

interface PlanComponentProps {
  onClose?: () => void
  actionLabel?: string
  isCheckoutActive?: boolean
  defaultTab?: UpgradeTab
}

const CheckCircleIcon = () => (
  <svg width='20' height='20' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM11.0234 5.71406L7.73281 10.2766C7.68682 10.3408 7.62619 10.3931 7.55595 10.4291C7.48571 10.4652 7.40787 10.4841 7.32891 10.4841C7.24994 10.4841 7.17211 10.4652 7.10186 10.4291C7.03162 10.3931 6.97099 10.3408 6.925 10.2766L4.97656 7.57656C4.91719 7.49375 4.97656 7.37813 5.07812 7.37813H5.81094C5.97031 7.37813 6.12187 7.45469 6.21562 7.58594L7.32812 9.12969L9.78438 5.72344C9.87813 5.59375 10.0281 5.51562 10.1891 5.51562H10.9219C11.0234 5.51562 11.0828 5.63125 11.0234 5.71406Z'
      fill='#846FE1'
    />
  </svg>
)

function UpgradePlanCard({
  plan,
  isSelected,
  onClick,
  priceDisplay,
  intervalDisplay
}: {
  plan: LicenseTypeResponse
  isSelected: boolean
  onClick: () => void
  priceDisplay: string
  intervalDisplay: string | null
}) {
  const isEnterprise = plan.name === 'enterprise'
  const description = PLAN_DESCRIPTIONS[plan.name] || plan.description

  return (
    <button
      type='button'
      data-testid={`plan-card-${plan.name}`}
      data-selected={isSelected}
      onClick={onClick}
      className={`rounded-3xl px-6 py-4 text-left cursor-pointer transition-all h-[128px] bg-tokens-neutral-bg-grey-1 ${
        isSelected
          ? 'border-[1.5px] border-tokens-status-link-1'
          : 'border border-tokens-neutral-border-2 hover:border-tokens-primary-4/40'
      }`}
    >
      <div className='flex flex-col gap-6 h-full'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between'>
            <Typography
              fontFamily='geist'
              fontSize='titleMd'
              fontWeight='bold'
              className='text-tokens-neutral-text-primary'
            >
              {plan.label}
            </Typography>
            {isSelected && <CheckCircleIcon />}
          </div>
          <div className='flex items-baseline'>
            <Typography
              fontFamily='geist'
              fontSize={isEnterprise ? 'titleMd' : 'headlineSm'}
              fontWeight='bold'
              className='text-tokens-primary-2'
            >
              {priceDisplay}
            </Typography>
            {intervalDisplay && (
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='bold'
                className='text-tokens-primary-2'
              >
                {intervalDisplay}
              </Typography>
            )}
          </div>
        </div>
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          fontWeight='medium'
          className='text-tokens-neutral-text-primary'
        >
          {description}
        </Typography>
      </div>
    </button>
  )
}

function CreditRow({
  label,
  price,
  isSelected,
  onClick,
  isCustom = false,
  customInput,
  customPrice
}: {
  label: string
  price?: string
  isSelected: boolean
  onClick: () => void
  isCustom?: boolean
  customInput?: React.ReactNode
  customPrice?: string
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`w-full flex items-center px-4 py-5 rounded-[16px] transition-all cursor-pointer ${
        isSelected
          ? 'bg-tokens-neutral-fill-fill-4 border border-tokens-status-link-2'
          : 'bg-[rgba(189,189,189,0.08)]'
      }`}
    >
      <div className='flex-1 min-w-0 flex items-center gap-2'>
        {isSelected && <CheckCircleIcon />}
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          fontWeight='medium'
          className={`shrink-0 ${isCustom ? 'w-[104px]' : ''} ${isSelected ? 'text-tokens-neutral-text-primary' : 'text-tokens-neutral-text-quaternary'}`}
        >
          {label}
        </Typography>
        {isCustom && isSelected && customInput}
      </div>
      {(price || customPrice) && (
        <Typography
          fontFamily='geist'
          fontSize='titleSm'
          fontWeight='bold'
          className={`shrink-0 whitespace-nowrap pl-2 ${isSelected ? 'text-tokens-neutral-text-primary' : 'text-tokens-neutral-text-quaternary'}`}
        >
          {isCustom ? customPrice : price}
        </Typography>
      )}
    </button>
  )
}

export function PlanComponent({
  onClose,
  actionLabel = 'Next',
  isCheckoutActive = true,
  defaultTab = 'plan'
}: PlanComponentProps) {
  const isPlugin = bridge.isInPlugin()
  const [activeTab, setActiveTab] = useState<UpgradeTab>(defaultTab)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [billingPackage, setBillingPackage] = useState<BillingPackage>('monthly')
  const [teamSize] = useState('5')
  const { currentWorkspace } = useWorkspaceContext()
  const currentOrg = currentWorkspace
  const {
    billingInfo,
    isBillingAdmin,
    licenseTypes,
    licenseTypesLoading: isLoadingPlans,
    refetch: refetchBilling
  } = useBillingContext()
  const { openComplete } = useModalContext()

  const checkoutModeRef = useRef<'credits' | 'upgrade'>('credits')

  const handleVerificationSuccess = useCallback(() => {
    refetchBilling()
    onClose?.()

    const wasCreditPurchase = checkoutModeRef.current === 'credits'
    if (wasCreditPurchase) {
      BetterToast.success('Credits purchased successfully!')
    } else {
      openComplete({ pendingCreateWorkspace: selectedPlan === 'team' })
    }
  }, [onClose, openComplete, selectedPlan, refetchBilling])

  const handleVerificationFailure = useCallback(() => {
    const isCreditCheckout = checkoutModeRef.current === 'credits'
    BetterToast.error(
      isCreditCheckout
        ? 'Credit purchase was canceled. You can try again when you are ready.'
        : 'Subscription was not completed. Please try again.'
    )
  }, [])

  const { isLoading, checkoutStatus, startCheckout, cleanup } = useStripeCheckout({
    billingId: billingInfo?.id,
    onSuccess: handleVerificationSuccess,
    onFailure: handleVerificationFailure,
    isActive: isCheckoutActive
  })

  const {
    creditPacks,
    selectedFlexPack,
    selectPack: handleCreditPackClick,
    selectCustom: handleCustomCreditClick,
    customCredits,
    setCustomCredits,
    clearSelection: clearCreditSelection,
    isCustomFlexPack,
    isValidCustomAmount,
    customPriceCents,
    customAmountLimits,
    purchaseCredits,
    pricePerCreditCents
  } = useCreditPacks({
    billingId: billingInfo?.id,
    startCheckout
  })

  const displayPlans = useMemo(
    () =>
      [...licenseTypes]
        .filter(p => p.name !== 'flex')
        .sort((a, b) => (PLAN_DISPLAY_ORDER[a.name] ?? 99) - (PLAN_DISPLAY_ORDER[b.name] ?? 99)),
    [licenseTypes]
  )

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  const ensureCreditSelection = useCallback(() => {
    if (!selectedFlexPack && !isCustomFlexPack && creditPacks.length > 0) {
      handleCreditPackClick(creditPacks[0].id)
    }
  }, [selectedFlexPack, isCustomFlexPack, creditPacks, handleCreditPackClick])

  useEffect(() => {
    if (activeTab === 'credits') {
      ensureCreditSelection()
    }
  }, [activeTab, ensureCreditSelection])

  const handlePlanClick = (plan: string) => {
    setSelectedPlan(plan)
    clearCreditSelection()
  }

  useEffect(() => {
    if (!isLoadingPlans && licenseTypes.length === 1 && !selectedPlan) {
      setSelectedPlan(licenseTypes[0].name)
    }
  }, [isLoadingPlans, licenseTypes, selectedPlan])

  const calculatePrice = (plan: LicenseTypeResponse) => {
    const pricing = plan.metadata?.pricing
    const price = pricing?.price ?? plan.price ?? 0
    const interval = pricing?.interval ?? plan.interval ?? ''
    const perUserPrice = pricing?.per_user_price

    if (plan.name === 'enterprise' || interval === 'custom') {
      return 'Custom Pricing'
    } else if (plan.name === 'team' || interval === 'per_user_monthly') {
      const teamSizeNumber = parseInt(teamSize, 10)
      const userPrice = perUserPrice ?? price
      const monthlyTotal = teamSizeNumber * userPrice
      const packagePricing = getPackagePricing(pricing, billingPackage)
      const packageMonths = packagePricing?.package_months ?? SIX_MONTH_PACKAGE_MONTHS
      const fallbackDiscountPercent = packagePricing ? 0 : DEFAULT_SIX_MONTH_DISCOUNT_PERCENT
      const discountPercent =
        packagePricing?.discount_percent ??
        pricing?.six_month_discount_percent ??
        fallbackDiscountPercent
      const sixMonthPerUserPrice =
        packagePricing?.per_user_price ?? pricing?.per_user_six_month_price
      const displayPrice =
        billingPackage === 'six_month'
          ? teamSizeNumber *
            (sixMonthPerUserPrice ?? userPrice * packageMonths * (1 - discountPercent / 100))
          : monthlyTotal
      return `$${displayPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    } else if (price === 0) {
      return '$0'
    } else {
      const packagePricing = getPackagePricing(pricing, billingPackage)
      const packageMonths = packagePricing?.package_months ?? SIX_MONTH_PACKAGE_MONTHS
      const fallbackDiscountPercent = packagePricing ? 0 : DEFAULT_SIX_MONTH_DISCOUNT_PERCENT
      const discountPercent =
        packagePricing?.discount_percent ??
        pricing?.six_month_discount_percent ??
        fallbackDiscountPercent
      const sixMonthPrice =
        packagePricing?.price ?? pricing?.subscription_six_month_price ?? pricing?.six_month_price
      const displayPrice =
        billingPackage === 'six_month'
          ? (sixMonthPrice ?? price * packageMonths * (1 - discountPercent / 100))
          : price
      return `$${displayPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    }
  }

  const getIntervalDisplay = (plan: LicenseTypeResponse) => {
    if (plan.name === 'enterprise') return null
    return billingPackage === 'six_month' ? '/6 mo' : '/mo'
  }

  const handleUpgradeCheckout = async () => {
    if (selectedPlan === 'enterprise') {
      window.open('https://www.curietech.ai/contact-sales', '_blank')
      onClose?.()
      return
    }

    if (!billingInfo?.id) {
      BetterToast.error('Billing account not found. Please try again.')
      return
    }

    checkoutModeRef.current = 'upgrade'

    try {
      // startCheckout opens the tab synchronously (so it isn't popup-blocked);
      // the session-create runs as the producer, after which the tab navigates.
      const opened = await startCheckout(async () => {
        const successUrl = `${window.location.origin}/checkout-callback?status=success`
        const cancelUrl = `${window.location.origin}/checkout-callback?status=canceled`

        const response = await fetchWrapper(
          `/api/billing/${billingInfo.id}/create-subscription-checkout`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              account_type: selectedPlan!,
              team_size: selectedPlan === 'team' ? parseInt(teamSize, 10) : 1,
              billing_package: billingPackage,
              success_url: successUrl,
              cancel_url: cancelUrl
            })
          }
        )

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            extractApiErrorMessage(
              errorData,
              `Failed to create checkout session: ${response.status}`
            )
          )
        }

        const data = await response.json()
        if (!data.redirect_url) throw new Error('No redirect URL received from checkout session')
        return data.redirect_url
      })
      if (!opened) {
        BetterToast.error('Could not open checkout. Please allow popups and try again.')
      }
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const handleCreditPurchase = () => {
    checkoutModeRef.current = 'credits'
    purchaseCredits()
  }

  const handleClose = () => {
    cleanup()
    setSelectedPlan(null)
    setBillingPackage('monthly')
    onClose?.()
  }

  const handleTabChange = (tab: UpgradeTab) => {
    setActiveTab(tab)
    setSelectedPlan(null)
    setBillingPackage('monthly')
  }

  const handleCreditRowClick = (packId: string) => {
    handleCreditPackClick(packId)
    setSelectedPlan(null)
  }

  const handleCustomRowClick = () => {
    handleCustomCreditClick()
    setSelectedPlan(null)
  }

  if (!currentOrg) return null

  const subtitleText =
    activeTab === 'credits'
      ? `Pay as you go at $0.65 per Curie credit.`
      : 'Choose the one that fits your usage and team size.'

  const isActionDisabled =
    activeTab === 'plan'
      ? isLoading || !isBillingAdmin || !selectedPlan
      : isLoading ||
        !isBillingAdmin ||
        (!selectedFlexPack && !isCustomFlexPack) ||
        (isCustomFlexPack && !isValidCustomAmount)

  const loadingText =
    checkoutStatus === 'waitingForLicense'
      ? 'Activating your plan...'
      : checkoutStatus === 'verifying'
        ? 'Verifying payment...'
        : 'Waiting for payment...'

  const renderActionButton = () => {
    if (activeTab === 'plan') {
      if (selectedPlan === 'enterprise') {
        return (
          <Button
            variant='primary'
            aria-label='Upgrade to enterprise button'
            isDisabled={isLoading || !isBillingAdmin}
            onClick={handleUpgradeCheckout}
          >
            Contact Sales
          </Button>
        )
      }
      return (
        <Button
          variant='primary'
          aria-label={selectedPlan ? `Upgrade to ${selectedPlan} button` : 'Upgrade button'}
          isDisabled={isActionDisabled}
          onClick={handleUpgradeCheckout}
        >
          {isLoading && checkoutModeRef.current === 'upgrade' ? (
            <span className='flex items-center gap-2'>
              <LoadingIcon />
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='text-inherit'
              >
                {loadingText}
              </Typography>
            </span>
          ) : (
            actionLabel
          )}
        </Button>
      )
    }

    return (
      <Button
        variant='primary'
        aria-label='Purchase Credits button'
        isDisabled={isActionDisabled}
        onClick={handleCreditPurchase}
      >
        {isLoading && checkoutModeRef.current === 'credits' ? (
          <span className='flex items-center gap-2'>
            <LoadingIcon />
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-inherit'
            >
              {loadingText}
            </Typography>
          </span>
        ) : (
          actionLabel
        )}
      </Button>
    )
  }

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      {/* Sticky Header */}
      <div
        className={`flex-shrink-0 flex flex-col items-center ${isPlugin ? 'pt-6 px-3' : 'pt-[60px] px-[48px]'}`}
      >
        <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold'>
          Choose the Right Plan
        </Typography>

        {isBillingAdmin && (
          <div className='flex flex-col items-center gap-2 mt-4'>
            <SegmentControls
              value={activeTab}
              onValueChange={val => handleTabChange(val as UpgradeTab)}
            >
              <SegmentControlsList>
                <SegmentControlsTrigger value='plan' className='w-auto'>
                  Upgrade Plan
                </SegmentControlsTrigger>
                <SegmentControlsTrigger value='credits' className='w-auto'>
                  Buy Credits
                </SegmentControlsTrigger>
              </SegmentControlsList>
            </SegmentControls>
            {activeTab === 'plan' && (
              <SegmentControls
                value={billingPackage}
                onValueChange={val => setBillingPackage(val as BillingPackage)}
              >
                <SegmentControlsList>
                  <SegmentControlsTrigger value='monthly' className='w-auto'>
                    Monthly
                  </SegmentControlsTrigger>
                  <SegmentControlsTrigger value='six_month' className='w-auto'>
                    6 months
                  </SegmentControlsTrigger>
                </SegmentControlsList>
              </SegmentControls>
            )}
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-tokens-neutral-text-quaternary text-center whitespace-nowrap'
            >
              {subtitleText}
            </Typography>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div
        className={`flex-1 min-h-0 overflow-y-auto ${isPlugin ? 'px-3 pt-4 pb-6' : 'px-[48px] pt-8 pb-[48px]'}`}
      >
        {!isBillingAdmin && (
          <div className='flex flex-col items-center gap-4 text-center max-w-md mx-auto py-12'>
            <Typography
              fontFamily='geist'
              fontSize='titleMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-secondary'
            >
              Admin Access Required
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='text-tokens-neutral-text-tertiary'
            >
              Only billing admins can upgrade plans. Please contact your billing administrator to
              make changes to your subscription.
            </Typography>
          </div>
        )}

        {isBillingAdmin && isLoadingPlans && (
          <div className='flex flex-col items-center gap-4 py-12'>
            <LoadingIcon />
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='text-tokens-neutral-text-tertiary'
            >
              Loading available plans...
            </Typography>
          </div>
        )}

        {/* Both tabs rendered in a grid overlay so height = max of both, preventing layout shift */}
        {isBillingAdmin && !isLoadingPlans && (
          <div className='grid [&>*]:col-start-1 [&>*]:row-start-1'>
            {/* Upgrade Plan Tab */}
            <div
              className={
                activeTab !== 'plan' ? 'invisible pointer-events-none [&_*]:!transition-none' : ''
              }
            >
              {displayPlans.length === 0 ? (
                <div className='flex flex-col items-center gap-4 text-center max-w-md mx-auto py-12'>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleMd'
                    fontWeight='semibold'
                    className='text-tokens-neutral-text-secondary'
                  >
                    You&apos;re on the highest tier
                  </Typography>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    className='text-tokens-neutral-text-tertiary'
                  >
                    No upgrades are available for your current plan.
                  </Typography>
                </div>
              ) : (
                <div
                  className={
                    isPlugin
                      ? 'grid grid-cols-1 gap-4 w-full mx-auto'
                      : 'grid grid-cols-2 gap-6 w-[540px] mx-auto'
                  }
                >
                  {displayPlans.map(plan => (
                    <UpgradePlanCard
                      key={plan.name}
                      plan={plan}
                      isSelected={selectedPlan === plan.name}
                      onClick={() => handlePlanClick(plan.name)}
                      priceDisplay={calculatePrice(plan)}
                      intervalDisplay={getIntervalDisplay(plan)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Buy Credits Tab */}
            <div
              className={
                activeTab !== 'credits'
                  ? 'invisible pointer-events-none [&_*]:!transition-none'
                  : ''
              }
            >
              <div
                className={
                  isPlugin
                    ? 'flex flex-col gap-2 w-full mx-auto'
                    : 'flex flex-col gap-2 w-[440px] mx-auto'
                }
              >
                {creditPacks.map((pack: CreditPackInfo) => (
                  <CreditRow
                    key={pack.id}
                    label={`${pack.credits} Credits`}
                    price={pack.price_display}
                    isSelected={selectedFlexPack === pack.id && !isCustomFlexPack}
                    onClick={() => handleCreditRowClick(pack.id)}
                  />
                ))}
                <CreditRow
                  label='Custom Credits'
                  isSelected={isCustomFlexPack}
                  onClick={handleCustomRowClick}
                  isCustom
                  customInput={
                    <input
                      type='number'
                      min={customAmountLimits.min}
                      max={customAmountLimits.max}
                      value={customCredits}
                      onChange={e => setCustomCredits(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      placeholder={`Amount from ${customAmountLimits.min.toLocaleString('en-US')} to ${customAmountLimits.max.toLocaleString('en-US')}`}
                      autoFocus
                      className={`h-9 px-3 rounded-lg border border-tokens-neutral-border-1 text-sm text-tokens-neutral-text-primary focus:outline-none focus:border-tokens-status-link-1 bg-tokens-neutral-bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isPlugin ? 'w-full' : 'w-[240px]'}`}
                    />
                  }
                  customPrice={`$${(customPriceCents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Footer */}
      <ModalFooter
        className='flex-shrink-0'
        startContent={
          activeTab === 'plan' && displayPlans.length > 0 ? (
            <button
              type='button'
              onClick={() => window.open('https://www.curietech.ai/pricing', '_blank')}
              className='flex items-center gap-1.5 text-tokens-status-link-1 hover:opacity-70 transition-opacity'
            >
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='text-inherit'
              >
                Plan Details
              </Typography>
              <ExternalLink size={16} />
            </button>
          ) : undefined
        }
        endContent={
          <>
            {onClose && (
              <Button variant='outlined' isDisabled={isLoading} onClick={handleClose}>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  className='text-inherit'
                >
                  Not for now
                </Typography>
              </Button>
            )}
            {renderActionButton()}
          </>
        }
      />
    </div>
  )
}

interface PlanDialogProps {
  isOpen: boolean
  onClose: () => void
}

const PlanDialog = ({ isOpen, onClose }: PlanDialogProps) => {
  const isPlugin = bridge.isInPlugin()
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={
        isPlugin
          ? 'max-w-[calc(100vw-24px)] w-full h-[calc(100vh-32px)] overflow-hidden'
          : 'min-w-[636px] max-w-[840px] w-full h-[calc(100vh-120px)] overflow-hidden'
      }
      overlayClassName={isPlugin ? 'px-3' : 'px-[48px]'}
      hideCloseButton={false}
      disableOutsideClick
      rawContent
    >
      <PlanComponent onClose={onClose} isCheckoutActive={isOpen} />
    </Modal>
  )
}

export default PlanDialog
